import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ContractServices } from "../../../core/services/ContractServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { ContractFields, ContractListParams } from "./ContractDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Contracts.css";
import ContractFilters from "./ContractFilters";
import {
  isEmptyVal,
  displayField,
  displayDateField,
  displayAmountField,
  getLabelFor,
  displayYesNo,
  getMultiLabelFor,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import ContractSingleAction from "./ContractSingleAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  paymentFrequencyObjArr,
  contractTypeObjArr,
  contractForObjArr,
  onOffArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

function Contracts() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ContractFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    company: "",
    is_last_active_contract: "",
    is_internal_ad_allowed: "",
    is_external_ad_allowed: "",
    start_date: null,
    end_date: null,
    period_nature: "current",
    bill_payment_frequency: "",
    client_contract_type: "",
    contract_for: "",
    automate_bill: "",
    agreement_number: "",
    po_number: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<ContractListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setPage(1);
    getContracts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, [company]);

  async function getContracts(params: ContractListParams) {
    try {
      setLoading(true);
      const res = await ContractServices.getContracts(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(UtilityServices.hasPermission("create-contract"));
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteContract(contract: any) {
    try {
      let res = await ContractServices.deleteContract(contract?.id);
      setPage(1);
      getContracts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishContract(contract: any) {
    try {
      let status =
        contract?.is_last_active_contract == onOffArr.off
          ? onOffArr.on
          : onOffArr.off;

      let res = await ContractServices.publishContract(contract?.id, {
        is_active_contract: status,
      });
      setPage(1);
      getContracts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function refreshListData() {
    try {
      getContracts(getSearchParams());
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: ContractListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (company?.id) {
      params.company = company.id;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getContracts(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getContracts(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getContracts(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getContracts(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterContracts = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getContracts(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getContracts(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getContracts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-contract");
  const updatePermission = UtilityServices.hasPermission("update-contract");
  const deletePermission = UtilityServices.hasPermission("delete-contract");

  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
  };

  return (
    <div className="contracts customers ">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("CONTRACTS")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_CONTRACT)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content>
          <ContractFilters onSubmit={submitSearch} onClear={clearSearch} />
          <div className="row">
            <div className="col-md-4">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("COMPANY").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase() +
                      ", " +
                      t("COMPANY").toLowerCase() +
                      " " +
                      t("CODE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("MOBILE").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterContracts(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterContracts(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>

          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>

              <Column width={120} sortable resizable fixed>
                <CustomHeaderCell>
                  {t("START")} {t("DATE")}
                </CustomHeaderCell>
                <CustomCell dataKey="contract_from">
                  {(rowData: any) => (
                    <span>{displayDateField(rowData?.contract_from)}</span>
                  )}
                </CustomCell>
              </Column>

              <Column width={120} sortable resizable fixed>
                <CustomHeaderCell>
                  {t("END")} {t("DATE")}
                </CustomHeaderCell>
                <CustomCell dataKey="contract_to">
                  {(rowData: any) => (
                    <span>{displayDateField(rowData?.contract_to)}</span>
                  )}
                </CustomCell>
              </Column>

              <Column width={100} sortable>
                <CustomHeaderCell>
                  {t("BILLING")} {t("RATE")}
                </CustomHeaderCell>
                <CustomCell dataKey="contract_billing_rate">
                  {(rowData: any) => (
                    <span>
                      {displayAmountField(rowData?.contract_billing_rate)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={120}>
                <CustomHeaderCell>
                  {t("BILLING")} {t("FREQUENCY")}
                </CustomHeaderCell>
                <CustomCell dataKey="bill_payment_frequency">
                  {(rowData: any) => (
                    <span>
                      {displayField(
                        getLabelFor(
                          rowData?.bill_payment_frequency,
                          paymentFrequencyObjArr
                        )
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={120}>
                <CustomHeaderCell>
                  {t("CONTRACT")} {t("TYPE")}
                </CustomHeaderCell>
                <CustomCell dataKey="client_contract_type">
                  {(rowData: any) => (
                    <span>
                      {displayField(
                        getLabelFor(
                          rowData?.client_contract_type,
                          contractTypeObjArr
                        )
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160} resizable>
                <CustomHeaderCell>
                  {t("CONTRACT")} {t("FOR")}
                </CustomHeaderCell>
                <CustomCell dataKey="contract_for">
                  {(rowData: any) => (
                    <span
                      title={displayField(
                        getMultiLabelFor(
                          rowData?.contract_for,
                          contractForObjArr
                        )
                      )}
                    >
                      {displayField(
                        getMultiLabelFor(
                          rowData?.contract_for,
                          contractForObjArr
                        )
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell
                  title={t("LAST") + " " + t("ACTIVE") + " " + t("CONTRACT")}
                >
                  {t("PUBLISHED")}
                </CustomHeaderCell>
                <CustomCell dataKey="is_last_active_contract">
                  {(rowData: any) => (
                    <span>
                      {displayYesNo(rowData?.is_last_active_contract)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={110}>
                <CustomHeaderCell
                  title={t("EXTERNAL") + " " + t("AD") + " " + t("ALLOWED")}
                >
                  {t("EXT")} {t("AD")} {t("ALLOWED")}
                </CustomHeaderCell>
                <CustomCell dataKey="is_external_ad_allowed">
                  {(rowData: any) => (
                    <span>{displayYesNo(rowData?.is_external_ad_allowed)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <ContractSingleAction
                      contractData={rowData}
                      onDeleteContract={deleteContract}
                      onPublishContract={publishContract}
                      permission={permissionObj}
                      refreshList={refreshListData}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
    </div>
  );
}

export default Contracts;
